"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  ArrowRight,
  Braces,
  ChevronRight,
  CornerDownRight,
  ExternalLink,
  History,
  Loader2,
  Lock,
  MinusCircle,
  Repeat,
  RotateCw,
  SearchX,
  Send,
} from "lucide-react";
import {
  getWebhookDelivery,
  resendWebhookDelivery,
  retryWebhookDeliveryNow,
  type ApiError,
  type CannotResendReason,
  type DeliveryAttempt,
  type DeliveryDetail,
  type SkipReason,
} from "../../../services/merchantApi";
import {
  CodeBlock,
  ConfirmDialog,
  CopyButton,
  EmptyState,
  ErrorCard,
  InlineNotice,
  Section,
  SegmentedControl,
  StatusPill,
  Tag,
  TimeText,
  focusRing,
  primaryBtn,
} from "../ui";
import {
  formatAbsolute,
  formatCount,
  formatCountdown,
  formatDuration,
  formatUtc,
  hostOf,
  timeAgo,
  truncateMiddle,
} from "./format";
import { prettyJson } from "./jsonHighlight";
import {
  DELIVERY_STATUS_UI,
  SKIP_REASON_LABEL,
  TRIGGER_TAG,
  deliveryStatusLabel,
  fallbackHint,
} from "./vocab";
import { WebhooksGate } from "./WebhooksGate";
import { notifyWebhooksChanged } from "./useWebhookSummary";
import AttemptTimeline from "./AttemptTimeline";

// ────────────────────────────────────────────────────────────────────────────
// DeliveryDetailPage — /developers/webhooks/deliveries/:deliveryId
//
// The target of the "View delivery" link in failure emails, so it is a real
// page (deep-linkable, survives a reload) behind the same WebhooksGate as
// the rest of the webhooks area.
//
//   ← Back to deliveries           (restores the last list query)
//   header: pill · event type · tags · [Retry now] [Resend]
//   facts:  delivery id · endpoint · created (local + UTC) · completed/next
//   status summary (next attempt / skipped / failed + hint) · resend links
//   Attempts · Request (headers, signed-with, body Pretty|Raw) · Event
//
// Everything that came from the merchant's server (response body, headers,
// error text, URL) is rendered as React text children only.
// ────────────────────────────────────────────────────────────────────────────

const DELIVERY_ID_RE = /^whd_[0-9a-z]{9}[0-9a-f]{12}$/;
// Written by DeliveriesTab on every filter change.
const QUERY_STORAGE_KEY = "cashtap:webhooks:deliveriesQuery";
// history.state key used to hand a just-created resend to its own page, so
// the page paints at once without spending a read on the detail rate limit.
const SEED_STATE_KEY = "cashtapWebhookDelivery";
const SEED_MAX_AGE_MS = 15_000;
const IN_PROGRESS = new Set(["pending", "delivering", "retrying"]);

const CANNOT_RESEND_COPY: Record<CannotResendReason, string> = {
  in_progress: "Still retrying automatically — use Retry now.",
  endpoint_deleted: "The endpoint was deleted.",
  endpoint_disabled: "The endpoint is disabled. Re-enable it first.",
  event_expired: "The event payload is older than 35 days.",
  resend_limit_reached: "This delivery was already resent 5 times.",
};

const SKIP_EXPLANATION: Record<SkipReason, string> = {
  endpoint_paused:
    "The endpoint was paused when this event was due, so it was never sent. Resume the endpoint, then resend this event.",
  endpoint_disabled:
    "CashTap had disabled the endpoint after repeated failures, so this event was never sent. Re-enable the endpoint, then resend this event.",
  endpoint_deleted: "The endpoint was deleted before this event could be sent.",
  superseded:
    "The checkout session completed later, so this older event was not sent. Your server receives checkout.session.completed instead.",
  backlog_limit:
    "Too many deliveries were waiting for this endpoint, so this one was skipped. Fix the endpoint, then resend this event.",
};

const RETRY_SCHEDULE_NOTE =
  "Newest first. Failed attempts are retried after about 1 min, 5 min, 30 min, 2 h and 9 h.";

// Tinted button at primary size (DomainChipInput "Add" idiom).
const outlineBtn =
  "inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#61CE70]/10 hover:bg-[#61CE70]/15 text-[#61CE70] text-sm font-semibold border border-[#61CE70]/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
const textLink =
  "inline-flex items-center gap-1.5 text-xs font-semibold text-[#61CE70] hover:text-white transition-colors rounded-md";
const subHeading = "text-[11px] font-bold tracking-[0.16em] uppercase text-gray-500";

// ─── Small helpers ─────────────────────────────────────────────────────────

interface LoadError {
  message: string;
  requestId: string | null;
  retryAfter: number | null;
}

function toLoadError(e: unknown): LoadError {
  const err = e as ApiError | undefined;
  const rateLimited = err?.status === 429;
  return {
    message: rateLimited
      ? "Too many requests right now."
      : err?.message || "Something went wrong while loading this delivery.",
    requestId: err?.requestId || null,
    retryAfter: rateLimited ? err?.retryAfter || 60 : null,
  };
}

// Seconds left of a server-imposed wait; ticks once per second while
// running and stops at 0, so the controls re-enable on their own.
function useCountdown(): [number, (seconds: number) => void] {
  const [left, setLeft] = useState(0);
  const until = useRef(0);
  const running = left > 0;
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setLeft(Math.max(0, Math.ceil((until.current - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [running]);
  const start = useCallback((seconds: number) => {
    const s = Math.max(1, Math.ceil(seconds));
    until.current = Date.now() + s * 1000;
    setLeft(s);
  }, []);
  return [left, start];
}

function isDue(iso: string | null): boolean {
  if (!iso) return true;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) || ms <= Date.now();
}

function readSeed(state: unknown, id: string): DeliveryDetail | null {
  const entry = (state as Record<string, { delivery?: DeliveryDetail; at?: number } | undefined> | null)?.[
    SEED_STATE_KEY
  ];
  if (!entry || !entry.delivery || entry.delivery.id !== id || typeof entry.at !== "number") return null;
  if (Date.now() - entry.at > SEED_MAX_AGE_MS) return null;
  return entry.delivery;
}

function backToDeliveriesHref(): string {
  let saved = "";
  try {
    saved = sessionStorage.getItem(QUERY_STORAGE_KEY) || "";
  } catch {
    /* storage unavailable: plain tab link */
  }
  const sp = new URLSearchParams(saved.startsWith("?") ? saved.slice(1) : saved);
  sp.set("tab", "deliveries");
  sp.delete("new");
  return `/developers/webhooks?${sp.toString()}`;
}

function deliveriesListHref(params: Record<string, string | null | undefined>): string {
  const sp = new URLSearchParams({ tab: "deliveries" });
  for (const [k, v] of Object.entries(params)) if (v) sp.set(k, v);
  return `/developers/webhooks?${sp.toString()}`;
}

function deliveryHref(id: string): string {
  return `/developers/webhooks/deliveries/${encodeURIComponent(id)}`;
}

// The latest attempt that actually reached the network (interrupted stubs
// carry no response, signature or hint).
function newestRealAttempt(attempts: DeliveryAttempt[]): DeliveryAttempt | null {
  let best: DeliveryAttempt | null = null;
  let bestAt = -Infinity;
  const list = attempts || [];
  for (let i = 0; i < list.length; i++) {
    const a = list[i];
    if (a.outcome === "interrupted") continue;
    const t = Date.parse(a.startedAt);
    // Unparseable times fall back to array order (the API appends in order).
    const at = Number.isNaN(t) ? bestAt : t;
    if (at >= bestAt) {
      best = a;
      bestAt = at;
    }
  }
  return best;
}

function signedWithText(list: { version: number; last4: string }[]): string {
  return list.map((s) => `••••${s.last4} (v${s.version})`).join(" and ");
}

// How long to wait before the next poll. Every 5 s while the delivery is
// being sent or its next attempt is due within a minute; otherwise wake up
// shortly before the attempt, and at least once a minute. (The detail route
// allows 60 reads per 10 minutes — a flat 5 s poll during a 2-hour backoff
// would exhaust it in five minutes.)
function pollDelayFor(d: DeliveryDetail | null): number {
  if (!d || d.status === "delivering") return 5_000;
  const next = d.nextAttemptAt ? Date.parse(d.nextAttemptAt) : NaN;
  if (Number.isNaN(next)) return 5_000;
  const until = next - Date.now();
  if (until < -120_000) return 30_000; // overdue: our worker is behind
  if (until <= 60_000) return 5_000;
  return Math.min(60_000, Math.max(5_000, until - 55_000));
}

// ─── Page ──────────────────────────────────────────────────────────────────

/** Route component for /developers/webhooks/deliveries/:deliveryId (no props). */
export default function DeliveryDetailPage() {
  const { deliveryId = "" } = useParams<{ deliveryId: string }>();
  return (
    <WebhooksGate>
      {({ wallet, email }) => (
        // Keyed: navigating to another delivery (e.g. after Resend) starts clean.
        <DeliveryDetailView key={`${wallet}|${deliveryId}`} wallet={wallet} email={email} deliveryId={deliveryId} />
      )}
    </WebhooksGate>
  );
}

type ViewState =
  | { phase: "loading" }
  | { phase: "notFound" }
  | { phase: "error"; error: LoadError }
  | { phase: "ready"; delivery: DeliveryDetail };

function DeliveryDetailView({
  wallet,
  email,
  deliveryId,
}: {
  wallet: string;
  email: string | null;
  deliveryId: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef<HTMLDivElement>(null);
  const resendCaptionId = useId();

  const validId = DELIVERY_ID_RE.test(deliveryId);
  const [seed] = useState(() => (validId ? readSeed(location.state, deliveryId) : null));
  const [view, setView] = useState<ViewState>(() =>
    !validId ? { phase: "notFound" } : seed ? { phase: "ready", delivery: seed } : { phase: "loading" },
  );
  const backHref = useMemo(backToDeliveriesHref, []);

  const [loadCooldown, startLoadCooldown] = useCountdown();
  const [actionCooldown, startActionCooldown] = useCountdown();
  const seq = useRef(0);
  const inFlight = useRef(false);
  const pausedUntil = useRef(0);

  const d = view.phase === "ready" ? view.delivery : null;
  const dRef = useRef(d);
  dRef.current = d;

  // The app shell scrolls inside <main>, which the router's ScrollToTop
  // (window only) never resets — arriving from a long list would land
  // half-way down this page.
  useEffect(() => {
    const main = rootRef.current?.closest("main");
    if (main && main.scrollTop > 0) main.scrollTo({ top: 0 });
  }, []);

  const load = useCallback(
    async (silent: boolean) => {
      if (!validId) return;
      const my = ++seq.current;
      inFlight.current = true;
      if (!silent) setView((v) => (v.phase === "ready" ? v : { phase: "loading" }));
      try {
        const res = await getWebhookDelivery(wallet, email, deliveryId);
        if (my !== seq.current) return;
        setView({ phase: "ready", delivery: res.delivery });
      } catch (e) {
        if (my !== seq.current) return;
        if ((e as ApiError | undefined)?.status === 404) {
          setView({ phase: "notFound" });
          return;
        }
        const err = toLoadError(e);
        if (err.retryAfter) {
          pausedUntil.current = Date.now() + err.retryAfter * 1000;
          startLoadCooldown(err.retryAfter);
        }
        // A failed background poll keeps the page as it is.
        setView((v) => (silent && v.phase === "ready" ? v : { phase: "error", error: err }));
      } finally {
        if (my === seq.current) inFlight.current = false;
      }
    },
    [wallet, email, deliveryId, validId, startLoadCooldown],
  );

  useEffect(() => {
    if (validId && !seed) void load(false);
  }, [load, validId, seed]);

  // ── Polling while in progress (and only while the tab is visible) ──────
  const inProgress = !!d && IN_PROGRESS.has(d.status);
  const status = d?.status;
  const nextAttemptAt = d?.nextAttemptAt;
  useEffect(() => {
    if (!inProgress) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const clear = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const schedule = (ms: number) => {
      clear();
      timer = setTimeout(run, ms);
    };
    async function run() {
      timer = null;
      // Hidden: stop here; the visibility listener resumes the loop.
      if (cancelled || document.hidden) return;
      const wait = pausedUntil.current - Date.now();
      if (wait > 0) {
        schedule(wait);
        return;
      }
      await load(true);
      if (!cancelled) schedule(pollDelayFor(dRef.current));
    }
    const onVisible = () => {
      if (!document.hidden && !timer && !inFlight.current) void run();
    };
    document.addEventListener("visibilitychange", onVisible);
    schedule(pollDelayFor(dRef.current));
    return () => {
      cancelled = true;
      clear();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [inProgress, status, nextAttemptAt, load]);

  // ── Actions ────────────────────────────────────────────────────────────
  const [retryBusy, setRetryBusy] = useState(false);
  const [confirmResend, setConfirmResend] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendSlow, setResendSlow] = useState(false);
  const actionBusy = retryBusy || resendBusy;

  const onRetryNow = async () => {
    if (!d || actionBusy || actionCooldown > 0) return;
    setRetryBusy(true);
    try {
      const res = await retryWebhookDeliveryNow(wallet, email, d.id);
      setView((v) =>
        v.phase === "ready" && v.delivery.id === res.delivery.id
          ? { phase: "ready", delivery: { ...v.delivery, ...res.delivery } }
          : v,
      );
      toast.success("Retry scheduled. It will be sent within a few seconds.");
      notifyWebhooksChanged();
    } catch (e) {
      const err = e as ApiError | undefined;
      if (err?.status === 429) {
        startActionCooldown(err.retryAfter || 60);
      } else {
        toast.error(err?.message || "Couldn't schedule the retry.");
        void load(true);
      }
    } finally {
      setRetryBusy(false);
    }
  };

  const onResend = async () => {
    if (!d || actionBusy || actionCooldown > 0) return;
    setResendBusy(true);
    // The server makes one synchronous attempt (up to ~15 s).
    const slowTimer = setTimeout(() => setResendSlow(true), 6000);
    try {
      const res = await resendWebhookDelivery(wallet, email, d.id);
      const nd = res.delivery;
      notifyWebhooksChanged();
      if (nd.status === "succeeded") {
        toast.success(
          `Resent and delivered${nd.lastResponseStatus ? ` · HTTP ${nd.lastResponseStatus}` : ""}.`,
        );
      } else if (nd.status === "failed") {
        toast.error("Resent, but your server didn't accept it. See the attempt below.");
      } else {
        toast.success("Event resent as a new delivery.");
      }
      setConfirmResend(false);
      navigate(deliveryHref(nd.id), { state: { [SEED_STATE_KEY]: { delivery: nd, at: Date.now() } } });
    } catch (e) {
      const err = e as ApiError | undefined;
      if (err?.status === 429) {
        // Keep the dialog open: it shows the countdown and re-enables itself.
        startActionCooldown(err.retryAfter || 60);
      } else {
        setConfirmResend(false);
        toast.error(err?.message || "Couldn't resend this event.");
        void load(true);
      }
    } finally {
      clearTimeout(slowTimer);
      setResendSlow(false);
      setResendBusy(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  let content: ReactNode;
  if (view.phase === "loading") {
    content = <DetailSkeleton />;
  } else if (view.phase === "notFound") {
    content = (
      <EmptyState
        icon={SearchX}
        title="Delivery not found"
        hint="It may be older than 30 days or belong to another account. History is kept for 30 days."
        action={
          <Link to={backHref} className={primaryBtn}>
            <ArrowLeft className="w-4 h-4" /> Back to deliveries
          </Link>
        }
      />
    );
  } else if (view.phase === "error") {
    const cooling = !!view.error.retryAfter && loadCooldown > 0;
    content = (
      <ErrorCard
        title="Couldn't load this delivery"
        message={cooling ? `${view.error.message} Try again in ${formatCountdown(loadCooldown)}.` : view.error.message}
        requestId={view.error.requestId}
        onRetry={cooling ? undefined : () => void load(false)}
      />
    );
  } else {
    const dd = view.delivery;
    const reason = dd.cannotResendReason;
    const reasonCopy = !dd.canResend
      ? reason === "in_progress" && !dd.canRetryNow
        ? "Still in progress — it is retried automatically."
        : reason
          ? CANNOT_RESEND_COPY[reason] ?? "This delivery can't be resent."
          : "This delivery can't be resent."
      : null;

    content = (
      <>
        <DetailHeader
          d={dd}
          actions={
            <div className="flex flex-col items-stretch sm:items-end gap-1.5 shrink-0">
              <div className="flex flex-wrap gap-2 sm:justify-end">
                {dd.canRetryNow && (
                  <button
                    type="button"
                    onClick={onRetryNow}
                    disabled={actionBusy || actionCooldown > 0}
                    className={`${primaryBtn} justify-center`}
                  >
                    {retryBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
                    Retry now
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setConfirmResend(true)}
                  disabled={!dd.canResend || actionBusy || actionCooldown > 0}
                  title={reasonCopy || "Send this event again as a new delivery"}
                  aria-describedby={reasonCopy ? resendCaptionId : undefined}
                  className={`${outlineBtn} justify-center`}
                >
                  {resendBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Repeat className="w-4 h-4" />}
                  Resend
                </button>
              </div>
              {reasonCopy && (
                <p id={resendCaptionId} className="text-[11px] text-gray-500 sm:text-right sm:max-w-[16rem] leading-snug">
                  {reasonCopy}
                </p>
              )}
              {actionCooldown > 0 && (
                <p role="status" className="text-[11px] text-amber-300 sm:text-right tabular-nums">
                  Too many requests. Try again in {formatCountdown(actionCooldown)}.
                </p>
              )}
            </div>
          }
        />
        <FactsGrid d={dd} />
        <StatusSummary d={dd} />
        <RelatedDeliveries d={dd} />
        <div className="mt-6 space-y-6">
          <Section
            icon={<History className="w-4 h-4 text-[#61CE70]" />}
            title="Attempts"
            desc={dd.maxAttempts > 1 ? RETRY_SCHEDULE_NOTE : "Test sends and resends get exactly one attempt."}
          >
            {dd.status === "skipped" && (dd.attempts || []).length === 0 ? (
              <p className="text-sm text-gray-500">No attempt was made: this delivery was skipped.</p>
            ) : (
              <AttemptTimeline attempts={dd.attempts || []} maxAttempts={dd.maxAttempts} />
            )}
          </Section>
          <RequestSection d={dd} />
          <EventSection d={dd} />
        </div>
      </>
    );
  }

  return (
    <div ref={rootRef} className="max-w-5xl mx-auto py-8 sm:py-10 px-4">
      <Link
        to={backHref}
        className={`inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors rounded-md ${focusRing}`}
      >
        <ArrowLeft className="w-4 h-4" /> Back to deliveries
      </Link>
      <div className="mt-5">{content}</div>

      <ConfirmDialog
        open={confirmResend && !!d}
        onClose={() => {
          if (!resendBusy) setConfirmResend(false);
        }}
        onConfirm={onResend}
        title="Resend this event?"
        description="We'll send the same event again as a new delivery, signed with a fresh timestamp. Your server should dedupe on the event id."
        confirmLabel={resendBusy ? "Sending…" : "Resend event"}
        busy={resendBusy}
        confirmDisabled={actionCooldown > 0}
        icon={<Repeat className="w-5 h-5" />}
      >
        <div className="space-y-2.5">
          <InlineNotice icon={Send}>
            It goes to the endpoint's current URL as a single attempt, with no automatic retries.
          </InlineNotice>
          {resendSlow && (
            <p role="status" className="flex items-center gap-2 text-xs text-gray-400">
              <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
              Waiting for your server to respond (up to 15 seconds)…
            </p>
          )}
          {actionCooldown > 0 && (
            <p role="status" className="text-xs text-amber-300 tabular-nums">
              Too many resends right now. Try again in {formatCountdown(actionCooldown)}.
            </p>
          )}
        </div>
      </ConfirmDialog>
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────

function DetailHeader({ d, actions }: { d: DeliveryDetail; actions: ReactNode }) {
  const ui = DELIVERY_STATUS_UI[d.status] ?? DELIVERY_STATUS_UI.pending;
  const tag = TRIGGER_TAG[d.trigger];
  return (
    <header className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#61CE70]/20 via-[#50B060]/10 to-transparent border border-[#61CE70]/30 flex items-center justify-center shrink-0">
          <Send className="w-5 h-5 text-[#61CE70]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#61CE70] font-semibold">Webhook delivery</p>
          <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight break-words">{d.eventType}</h1>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <StatusPill ui={ui} label={deliveryStatusLabel(d)} size="lg" />
            {tag && <Tag tone={tag.tone}>{tag.label}</Tag>}
            {!d.livemode && d.trigger !== "test" && <Tag tone="gray">Test mode</Tag>}
          </div>
        </div>
      </div>
      {actions}
    </header>
  );
}

// ─── Facts (ids + times, local AND UTC as visible text) ────────────────────

function FactTile({ label, children, copy, copyLabel }: { label: string; children: ReactNode; copy?: string; copyLabel?: string }) {
  return (
    <div className="rounded-xl bg-black/20 border border-gray-800/60 px-3.5 py-3 min-w-0">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-500">{label}</p>
          <div className="mt-1 min-w-0">{children}</div>
        </div>
        {copy && <CopyButton text={copy} label={copyLabel || `Copy ${label.toLowerCase()}`} className="-my-1.5 -mr-2" />}
      </div>
    </div>
  );
}

function TimeValue({ iso, extra }: { iso: string; extra?: ReactNode }) {
  return (
    <>
      <p className="font-mono text-[12.5px] text-white tabular-nums">
        {formatAbsolute(iso)}
        {extra}
      </p>
      <p className="font-mono text-[11px] text-gray-500 break-all tabular-nums">UTC {formatUtc(iso)}</p>
    </>
  );
}

function FactsGrid({ d }: { d: DeliveryDetail }) {
  const host = hostOf(d.endpointUrl);
  const inProgress = IN_PROGRESS.has(d.status);

  let fourth: ReactNode;
  if (inProgress) {
    if (d.status === "delivering") {
      fourth = (
        <FactTile label="Next attempt">
          <p className="text-[12.5px] text-blue-300">Sending now…</p>
        </FactTile>
      );
    } else if (d.nextAttemptAt) {
      fourth = (
        <FactTile label="Next attempt" copy={formatUtc(d.nextAttemptAt)} copyLabel="Copy next attempt time (UTC)">
          <TimeValue
            iso={d.nextAttemptAt}
            extra={
              <span className="font-sans text-gray-500">
                {" "}
                · {isDue(d.nextAttemptAt) ? "due now" : timeAgo(d.nextAttemptAt)}
              </span>
            }
          />
        </FactTile>
      );
    }
  } else if (d.completedAt) {
    fourth = (
      <FactTile label="Completed" copy={formatUtc(d.completedAt)} copyLabel="Copy completion time (UTC)">
        <TimeValue iso={d.completedAt} />
      </FactTile>
    );
  } else if (d.lastAttemptAt) {
    fourth = (
      <FactTile label="Last attempt" copy={formatUtc(d.lastAttemptAt)} copyLabel="Copy last attempt time (UTC)">
        <TimeValue iso={d.lastAttemptAt} />
      </FactTile>
    );
  }

  return (
    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
      <FactTile label="Delivery ID" copy={d.id} copyLabel="Copy delivery id">
        <p className="font-mono text-[12.5px] text-white break-all">{d.id}</p>
      </FactTile>
      <FactTile label="Endpoint" copy={d.endpointUrl} copyLabel="Copy endpoint URL">
        <p className="text-[13px] text-white font-medium truncate">{host || "—"}</p>
        <p className="font-mono text-[11px] text-gray-500 break-all">{d.endpointUrl}</p>
        <Link
          to={`/developers/webhooks/endpoints/${encodeURIComponent(d.endpointId)}`}
          className={`mt-1.5 ${textLink} ${focusRing}`}
        >
          Endpoint settings <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </FactTile>
      <FactTile label="Created" copy={formatUtc(d.createdAt)} copyLabel="Copy creation time (UTC)">
        <TimeValue iso={d.createdAt} />
      </FactTile>
      {fourth}
    </div>
  );
}

// ─── Status summary ────────────────────────────────────────────────────────

function StatusSummary({ d }: { d: DeliveryDetail }) {
  const last = newestRealAttempt(d.attempts || []);
  const max = Math.max(1, d.maxAttempts || 1);
  const errMsg = d.lastErrorMessage || last?.errorMessage || null;
  const hint = last?.hint || fallbackHint(d.lastErrorCode ?? last?.errorCode, d.lastResponseStatus ?? last?.responseStatus);
  const nextNumber = Math.min(max, d.attemptCount + 1);

  const lastFailure =
    errMsg && d.attemptCount > 0 ? (
      <p>
        <span className="text-gray-300">Attempt {d.attemptCount} failed:</span> {errMsg}
      </p>
    ) : null;
  const hintLine = hint ? <p className="mt-1.5 text-xs opacity-90">{hint}</p> : null;

  let notice: ReactNode = null;
  if (d.delayed && (d.status === "pending" || d.status === "retrying")) {
    notice = (
      <InlineNotice tone="warn" icon={History} title="Queued longer than expected" role="status">
        <p>
          CashTap's delivery worker is running behind. This delivery is still queued and will be sent as soon
          as it catches up — nothing is lost.
        </p>
        {lastFailure && <div className="mt-1.5">{lastFailure}</div>}
      </InlineNotice>
    );
  } else if (d.status === "delivering") {
    notice = (
      <InlineNotice tone="info" icon={Send} title={`Sending attempt ${nextNumber} of ${max} now…`} role="status">
        This page updates on its own.
      </InlineNotice>
    );
  } else if (d.status === "pending") {
    notice = (
      <InlineNotice
        tone="warn"
        icon={History}
        title={isDue(d.nextAttemptAt) ? "First attempt any moment now" : `First attempt ${timeAgo(d.nextAttemptAt)}`}
        role="status"
      >
        Queued for delivery. This page updates on its own.
      </InlineNotice>
    );
  } else if (d.status === "retrying") {
    const when = isDue(d.nextAttemptAt) ? "any moment now" : timeAgo(d.nextAttemptAt);
    notice = (
      <InlineNotice tone="warn" icon={RotateCw} title={`Next attempt ${when} (attempt ${nextNumber} of ${max})`} role="status">
        {lastFailure}
        {hintLine}
        {d.canRetryNow && (
          <p className="mt-1.5 text-xs">Fixed your endpoint? Retry now sends the next attempt immediately.</p>
        )}
      </InlineNotice>
    );
  } else if (d.status === "failed") {
    notice = (
      <InlineNotice
        tone="error"
        title={
          max > 1
            ? `Failed · all ${max} attempts used`
            : d.trigger === "test"
              ? "The test attempt failed"
              : "The attempt failed"
        }
      >
        {errMsg && <p>{errMsg}</p>}
        {hintLine}
        {d.canResend && (
          <p className="mt-1.5 text-xs">Once your endpoint is fixed, use Resend to send this event again.</p>
        )}
      </InlineNotice>
    );
  } else if (d.status === "skipped") {
    const reason = d.skipReason;
    notice = (
      <InlineNotice
        icon={MinusCircle}
        title={`Skipped · ${reason ? SKIP_REASON_LABEL[reason] ?? reason : "not sent"}`}
      >
        {reason ? SKIP_EXPLANATION[reason] ?? "This event was not sent." : "This event was not sent."}
      </InlineNotice>
    );
  } else if (d.status === "succeeded") {
    const status = d.lastResponseStatus ?? last?.responseStatus ?? null;
    const dur = d.lastDurationMs ?? last?.durationMs ?? null;
    notice = (
      <InlineNotice
        tone="success"
        title={`Delivered${status ? ` · HTTP ${status}` : ""}${dur != null ? ` · ${formatDuration(dur)}` : ""}`}
      >
        Your server acknowledged this event
        {d.attemptCount > 1 ? ` on attempt ${d.attemptCount} of ${max}` : ""}.
      </InlineNotice>
    );
  }

  return notice ? <div className="mt-4">{notice}</div> : null;
}

// ─── Resend links ──────────────────────────────────────────────────────────

function RelatedDeliveries({ d }: { d: DeliveryDetail }) {
  const resends = d.resends || [];
  if (!d.resendOf && resends.length === 0) return null;
  return (
    <div className="mt-4 space-y-3">
      {d.resendOf && (
        <InlineNotice icon={CornerDownRight} title="Manual resend">
          This is a resend of{" "}
          <Link
            to={deliveryHref(d.resendOf)}
            className={`font-mono text-gray-200 underline decoration-gray-600 underline-offset-2 hover:text-white rounded-sm ${focusRing}`}
          >
            {truncateMiddle(d.resendOf, 12, 6)}
          </Link>
          . It carries the same event id with a new delivery id and a fresh signature timestamp.
        </InlineNotice>
      )}
      {resends.length > 0 && (
        <div className="rounded-xl border border-gray-800/60 bg-black/20 overflow-hidden">
          <p className={`px-4 pt-3 pb-2 ${subHeading}`}>
            Resent {resends.length} {resends.length === 1 ? "time" : "times"}
          </p>
          <ul className="divide-y divide-gray-800/50 border-t border-gray-800/50">
            {resends.map((r) => (
              <li key={r.id}>
                <Link
                  to={deliveryHref(r.id)}
                  className="group flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.02] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#61CE70]/60"
                >
                  <StatusPill ui={DELIVERY_STATUS_UI[r.status] ?? DELIVERY_STATUS_UI.pending} className="shrink-0" />
                  <span className="min-w-0 truncate font-mono text-xs text-gray-300">{truncateMiddle(r.id, 12, 6)}</span>
                  <span className="ml-auto shrink-0 text-xs text-gray-500">
                    <TimeText iso={r.createdAt} interactive={false} />
                  </span>
                  <ChevronRight className="w-4 h-4 shrink-0 text-gray-600 group-hover:text-gray-400" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Request ───────────────────────────────────────────────────────────────

function KeyValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-gray-500 text-xs sm:pt-0.5 mt-2 sm:mt-0">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}

function RequestSection({ d }: { d: DeliveryDetail }) {
  const req = d.request;
  const [bodyView, setBodyView] = useState<"pretty" | "raw">("pretty");
  const body = req?.body ?? null;
  const bodyIsJson = useMemo(() => body != null && prettyJson(body).isJson, [body]);
  const headerEntries = req?.headers ? Object.entries(req.headers) : [];
  const last = newestRealAttempt(d.attempts || []);
  const signedWith = last && last.signedWith && last.signedWith.length > 0 ? last.signedWith : null;
  const url = req?.url || d.endpointUrl;
  const safeHref = typeof url === "string" && url.startsWith("https://") ? url : null;
  const sha = req?.bodySha256 || "";

  return (
    <Section
      icon={<Send className="w-4 h-4 text-[#61CE70]" />}
      title="Request"
      desc="What CashTap sent on the latest attempt. Every attempt signs the same body with a fresh timestamp."
    >
      <dl className="grid grid-cols-1 sm:grid-cols-[7.5rem_minmax(0,1fr)] gap-x-4 sm:gap-y-2.5 text-sm">
        <KeyValue label="Method">
          <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wider bg-[#61CE70]/15 text-[#61CE70] border border-[#61CE70]/25">
            {req?.method || "POST"}
          </span>
        </KeyValue>
        <KeyValue label="URL">
          <div className="flex items-start gap-1">
            <span className="min-w-0 flex-1 font-mono text-[12.5px] text-gray-200 break-all pt-0.5">{url}</span>
            <CopyButton text={url} label="Copy URL" className="-my-2" />
            {safeHref && (
              <a
                href={safeHref}
                target="_blank"
                rel="noopener noreferrer nofollow"
                title="Open in a new tab (your browser sends a GET, not the webhook)"
                aria-label="Open endpoint URL in a new tab"
                className="shrink-0 -my-2 -mr-2 p-2.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </KeyValue>
        {signedWith && (
          <KeyValue label="Signed with">
            <span className="font-mono text-[12.5px] text-gray-200">{signedWithText(signedWith)}</span>
            {signedWith.length > 1 && (
              <span className="block text-xs text-gray-500 mt-0.5">
                Both secrets signed this request during the 24-hour rotation overlap.
              </span>
            )}
          </KeyValue>
        )}
      </dl>

      {headerEntries.length > 0 && (
        <>
          <p className={`mt-6 mb-2 ${subHeading}`}>Headers</p>
          <div className="rounded-xl border border-gray-800 overflow-hidden">
            <table className="w-full table-fixed text-xs">
              <thead className="bg-gray-900/60 text-gray-400">
                <tr>
                  <th scope="col" className="w-2/5 sm:w-1/3 text-left px-3 py-2 font-semibold">
                    Header
                  </th>
                  <th scope="col" className="text-left px-3 py-2 font-semibold">
                    Value
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/70">
                {headerEntries.map(([name, value]) => (
                  <tr key={name}>
                    <td className="px-3 py-2 align-top font-mono text-gray-400 break-all">{name}</td>
                    <td className="px-3 py-2 align-top font-mono text-gray-200 break-all">{String(value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-gray-500 leading-relaxed">
            The signature is shortened for display — a full signature plus the body could be replayed. Verify
            real requests with your signing secret.
          </p>
        </>
      )}

      <div className="mt-6 mb-1 flex items-center justify-between gap-3 flex-wrap">
        <p className={subHeading}>Body</p>
        {body != null && bodyIsJson && (
          <SegmentedControl<"pretty" | "raw">
            value={bodyView}
            onChange={setBodyView}
            options={[
              { value: "pretty", label: "Pretty" },
              { value: "raw", label: "Raw" },
            ]}
            ariaLabel="Body view"
          />
        )}
      </div>
      {req?.bodyExpired || body == null ? (
        <InlineNotice className="mt-2">The payload is no longer stored (kept 35 days).</InlineNotice>
      ) : (
        // Copy ALWAYS copies the string exactly as the API returned it —
        // never the prettified text.
        <CodeBlock
          key={bodyView}
          language="json"
          code={body}
          copyText={body}
          highlight={bodyView === "pretty"}
          wrap
          collapsedMaxLines={bodyView === "pretty" ? 40 : 12}
        />
      )}
      {sha && (
        <p className="text-[11px] text-gray-500 leading-relaxed">
          Signed bytes: <span className="font-mono text-gray-400 tabular-nums">{formatCount(req.bodyBytes)} B</span>{" "}
          · sha256 <span className="font-mono text-gray-400">{sha.slice(0, 12)}…</span>
          <CopyButton text={sha} label="Copy full sha256" variant="text" className="ml-1 -my-1 align-middle" />
          — your server must verify the signature over the exact raw bytes it received, not re-serialised JSON.
        </p>
      )}
      {req?.bodyMasked && body != null && (
        <InlineNotice icon={Lock} className="mt-3">
          customer_email is masked in the dashboard. The byte count and hash describe the unmasked payload your
          server received.
        </InlineNotice>
      )}
    </Section>
  );
}

// ─── Event ─────────────────────────────────────────────────────────────────

function EventSection({ d }: { d: DeliveryDetail }) {
  const ev = d.event;
  const eventId = ev?.id || d.eventId;
  const livemode = ev ? ev.livemode : d.livemode;
  const createdIso =
    ev && Number.isFinite(ev.created) && ev.created > 0 ? new Date(ev.created * 1000).toISOString() : null;
  // History links follow the namespace THIS delivery lives in (test sends
  // and their resends are only listed under Test events).
  const modeParam = d.livemode ? null : "test";

  return (
    <Section
      icon={<Braces className="w-4 h-4 text-[#61CE70]" />}
      title="Event"
      desc="Retries and resends reuse this event id — dedupe on it, not on the delivery id."
    >
      <dl className="grid grid-cols-1 sm:grid-cols-[7.5rem_minmax(0,1fr)] gap-x-4 sm:gap-y-2.5 text-sm">
        <KeyValue label="Event ID">
          <div className="flex items-start gap-1">
            <span className="min-w-0 flex-1 font-mono text-[12.5px] text-gray-200 break-all pt-0.5">{eventId}</span>
            <CopyButton text={eventId} label="Copy event id" className="-my-2" />
          </div>
        </KeyValue>
        <KeyValue label="Type">
          <span className="font-mono text-[12.5px] text-gray-200 break-all">{ev?.type || d.eventType}</span>
        </KeyValue>
        {createdIso && (
          <KeyValue label="Created">
            <span className="font-mono text-[12.5px] text-gray-200 tabular-nums">{formatAbsolute(createdIso)}</span>
            <span className="font-mono text-[11px] text-gray-500 tabular-nums"> · UTC {formatUtc(createdIso)}</span>
          </KeyValue>
        )}
        {ev?.apiVersion && (
          <KeyValue label="API version">
            <span className="font-mono text-[12.5px] text-gray-200">{ev.apiVersion}</span>
          </KeyValue>
        )}
        <KeyValue label="Mode">
          <span className="text-[13px] text-gray-200">{livemode ? "Live" : "Test (livemode: false)"}</span>
        </KeyValue>
        <KeyValue label="Session">
          {d.sessionId ? (
            <div className="flex items-start gap-1">
              <span className="min-w-0 flex-1 font-mono text-[12.5px] text-gray-200 break-all pt-0.5">{d.sessionId}</span>
              <CopyButton text={d.sessionId} label="Copy session id" className="-my-2" />
            </div>
          ) : (
            <span className="text-[13px] text-gray-500">—</span>
          )}
        </KeyValue>
      </dl>
      <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
        {d.sessionId && (
          <Link to={deliveriesListHref({ session: d.sessionId, mode: modeParam })} className={`${textLink} ${focusRing}`}>
            All deliveries for this session <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
        {eventId && (
          <Link to={deliveriesListHref({ event: eventId, mode: modeParam })} className={`${textLink} ${focusRing}`}>
            All deliveries of this event <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
    </Section>
  );
}

// ─── Loading skeleton (mirrors the loaded layout) ──────────────────────────

function DetailSkeleton() {
  return (
    <>
      <p role="status" className="sr-only">
        Loading delivery…
      </p>
      <div aria-hidden="true" className="animate-pulse">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gray-800/70 shrink-0" />
          <div className="flex-1 min-w-0 space-y-2.5">
            <div className="h-2.5 w-28 rounded bg-gray-800/60" />
            <div className="h-6 w-72 max-w-full rounded bg-gray-800/70" />
            <div className="h-6 w-44 rounded-full bg-gray-800/50" />
          </div>
          <div className="hidden sm:flex gap-2">
            <div className="h-9 w-28 rounded-xl bg-gray-800/60" />
            <div className="h-9 w-24 rounded-xl bg-gray-800/40" />
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-xl border border-gray-800/60 bg-black/20 px-3.5 py-3">
              <div className="h-2 w-20 rounded bg-gray-800/70" />
              <div className="h-3 w-48 max-w-full rounded bg-gray-800/50 mt-2.5" />
              <div className="h-2.5 w-36 max-w-full rounded bg-gray-800/30 mt-2" />
            </div>
          ))}
        </div>
        <div className="mt-4 h-20 rounded-xl border border-gray-800/60 bg-gray-800/20" />
        <div className="mt-6 space-y-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-2xl border border-gray-800/80 bg-gradient-to-br from-[#0F1419]/70 to-[#0B0F16]/70 p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-gray-800/60" />
                <div className="space-y-2 flex-1">
                  <div className="h-3 w-24 rounded bg-gray-800/70" />
                  <div className="h-2.5 w-64 max-w-full rounded bg-gray-800/40" />
                </div>
              </div>
              <div className="mt-5 space-y-2.5">
                <div className="h-3 w-full rounded bg-gray-800/40" />
                <div className="h-3 w-5/6 rounded bg-gray-800/30" />
                <div className="h-3 w-2/3 rounded bg-gray-800/30" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
